import { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  ArrowRight,
  CircleCheck,
  Lock,
  Award,
  Truck,
  MapPin,
  Clock,
  Home as HomeIcon,
  Wallet,
  Scale,
  Timer,
  Lightbulb,
  Boxes,
  Hand,
  AlertTriangle,
  ListChecks,
  Layers as LayersIcon,
  DoorOpen,
  PackageCheck,
} from "lucide-react";

const PRODUCT_SLUGS = [
  "pack-cartons-demenagement-t1",
  "pack-cartons-demenagement-t2",
  "pack-cartons-demenagement-t3",
  "pack-cartons-demenagement-maison",
];

const PacksCartons = () => {
  useDocumentTitle(
    "Kits cartons déménagement T1 T2 T3 maison | cartonmove",
    "Kits cartons clé en main pour studio, T2, T3 et maison. Tout le matériel de déménagement réuni en un seul kit pour Paris et l'Île-de-France."
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const items = PRODUCT_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div data-testid="packs-cartons-page">
      <CategoriesSubnav />

      {/* TOP — H1 + produits + intro */}
      <section className="section-pad bg-white" id="produits">
        <div className="container-cdm">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline justify-center">Boutique cartonmove</div>
            <h1 className="mt-3 text-[28px] sm:text-[33px] md:text-[48px] font-extrabold text-[#0f1d2b] leading-tight">
              Kit cartons déménagement
            </h1>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="packs-grid">
              {items.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty ctaLabel="Acheter" />
              ))}
            </div>
          )}

          {/* Intro */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Retrouvez tous nos <strong>packs de cartons de déménagement</strong> pour regrouper,
              sécuriser et transférer vos volumes. Des solutions complètes, robustes et prêtes à
              l'emploi pour simplifier l'organisation de votre déménagement à <strong>Paris</strong> et
              en <strong>Île-de-France</strong>.
            </p>
          </div>

          {/* CTA Big Déménagement */}
          <div className="mt-12">
            <BigDemenageurCTA testId="packs-bigdem-cta" />
          </div>

          {/* LIVRAISON IDF */}
          <div className="mt-12 max-w-4xl mx-auto bg-white border border-[#e6edf3] rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="overline">Service de livraison</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Service d'expédition de vos kits de cartons sur Paris et sa région
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Notre service logistique s'occupe de la livraison de vos assortiments d'emballage et
              cartons prêts à l'usage, spécifiquement sur le secteur francilien.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Pour s'ajuster au mieux à vos contraintes, nous fixons en commun une heure de passage
              bien définie. Vos modules de conditionnement complets vous sont livrés au moment choisi,
              ce qui vous assure de commencer la mise en boîte de vos affaires en toute tranquillité
              et sans retard.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">
              Les garanties de notre service de transport&nbsp;:
            </h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="packs-livraison-highlights">
              {[
                { icon: Truck, title: "Personnel interne", desc: "Livraison gérée de bout en bout par nos agents de transport." },
                { icon: MapPin, title: "Zone géographique", desc: "Expédition réservée uniquement aux résidents de l'Île-de-France." },
                { icon: Clock, title: "Planification agile", desc: "Calage de la date et de l'heure en fonction de votre agenda." },
                { icon: HomeIcon, title: "Remise sur place", desc: "Matériel acheminé et déposé directement dans vos pièces de vie ou vos bureaux." },
              ].map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="flex items-start gap-3 bg-[#f7fafc] rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[14.5px] font-extrabold text-[#0f1d2b]">{title}</div>
                    <div className="text-[13.5px] text-[#3a4a5a] leading-[1.55] mt-0.5">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEO sections */}
      <section className="section-pad bg-[#EEF3F7] border-t border-[#e6edf3]">
        <div className="container-cdm max-w-4xl space-y-10">
          {/* Pourquoi choisir nos packs */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Pourquoi choisir nos packs prêts à déménager&nbsp;?
            </h2>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: Wallet,
                  title: "Économique",
                  desc: "Les kits complets sont plus avantageux que l'achat de cartons et d'accessoires à l'unité.",
                },
                {
                  icon: Scale,
                  title: "Équilibré",
                  desc: "Chaque pack contient le juste ratio entre grands cartons (pour le linge, l'électroménager) et cartons renforcés (pour les livres, les objets lourds).",
                },
                {
                  icon: Timer,
                  title: "Gain de temps",
                  desc: "Plus besoin de calculer, vous sélectionnez votre surface et vous recevez tout le nécessaire en une seule livraison.",
                },
              ].map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="bg-white rounded-2xl border border-[#e6edf3] p-5 sm:p-6 flex flex-col gap-3"
                >
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#eaf3fb] text-[#237cb5]">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div className="text-base font-extrabold text-[#0f1d2b]">{title}</div>
                  <p className="text-[14px] text-[#3a4a5a] leading-[1.7]">{desc}</p>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-2xl bg-[#FFF6EE] border border-[#FFE0C2] p-5 sm:p-6 flex items-start gap-4">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white shrink-0">
                <Lightbulb className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[12px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                  Le conseil de l'expert
                </div>
                <p className="mt-2 text-[#3a4a5a] text-[15px] leading-[1.8]">
                  Vous accumulez des objets depuis de nombreuses années ou vous possédez beaucoup de
                  vaisselle&nbsp;? N'hésitez pas à choisir le <strong>pack de la taille supérieure</strong>{" "}
                  ou à ajouter quelques cartons spécifiques (comme notre <strong>carton penderie</strong>{" "}
                  pour vos vêtements sur cintres).
                </p>
              </div>
            </div>
          </article>

          {/* Guide de mise en boîte */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Guide de mise en boîte&nbsp;: comment optimiser vos packs cartons&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Recevoir le bon pack est une excellente première étape. Savoir comment le remplir comme
              un professionnel du déménagement vous garantit un{" "}
              <strong>gain de place maximal</strong> et une <strong>sécurité totale</strong> pour vos biens.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">
              Les règles d'or pour un colisage réussi&nbsp;:
            </h3>
            <ul className="mt-4 space-y-4" data-testid="packs-regles-or">
              {[
                {
                  icon: Boxes,
                  title: "La répartition du poids",
                  desc: "Placez toujours les objets les plus lourds (livres, dossiers, vaisselle) au fond du carton, et les éléments plus légers (vêtements, linge de maison) sur le dessus.",
                },
                {
                  icon: Hand,
                  title: "Le test du secouement",
                  desc: "Une fois votre carton rempli, fermez les rabats et secouez-le légèrement. Si vous entendez ou sentez du mouvement à l'intérieur, comblez les espaces vides avec du papier froissé ou du linge pour éviter la casse.",
                },
                {
                  icon: AlertTriangle,
                  title: "Ne surchargez pas les grands volumes",
                  desc: "Les grands cartons de votre pack sont conçus pour les objets volumineux mais légers (couettes, oreillers, peluches). Remplis de livres, ils deviendraient intransportables et risqueraient de céder sous le poids.",
                },
                {
                  icon: ListChecks,
                  title: 'La technique de fermeture en "H"',
                  desc: "Pour scotcher vos cartons de manière professionnelle, appliquez une bande centrale sur la jointure des rabats, puis une bande sur chaque bord latéral. C'est la seule méthode qui garantit que le fond ne lâchera pas.",
                },
              ].map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="flex items-start gap-4 bg-[#f7fafc] rounded-xl border border-[#e6edf3] p-4 sm:p-5"
                >
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[15px] font-extrabold text-[#0f1d2b]">{title}</div>
                    <p className="mt-1 text-[14px] text-[#3a4a5a] leading-[1.7]">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </article>

          {/* Stocker les packs avant le jour J */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Comment stocker vos packs avant le jour J&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Une fois vos cartons montés et remplis, une bonne gestion de votre espace intérieur vous
              évitera de vous laisser envahir pendant les préparatifs.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">
              Nos astuces de rangement&nbsp;:
            </h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="packs-astuces">
              {[
                {
                  icon: LayersIcon,
                  title: "Empilez par catégorie",
                  desc: "Créez des colonnes de cartons de tailles identiques en plaçant toujours les plus lourds à la base et les plus légers au sommet (max 4 à 5 cartons de hauteur).",
                },
                {
                  icon: DoorOpen,
                  title: "Dégagez les axes de passage",
                  desc: "Stockez vos piles de cartons le long des murs de chaque pièce pour laisser les couloirs et les accès aux portes totalement libres pour les déménageurs.",
                },
                {
                  icon: PackageCheck,
                  title: 'La zone "Dernier Carton"',
                  desc: "Gardez un carton ouvert dans l'entrée pour y mettre les objets du quotidien jusqu'à la dernière minute (chargeurs, papiers importants, trousse de secours, clés).",
                },
              ].map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="bg-white rounded-2xl border border-[#e6edf3] p-5 flex flex-col gap-3"
                >
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#eaf3fb] text-[#237cb5]">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div className="text-base font-extrabold text-[#0f1d2b]">{title}</div>
                  <p className="text-[14px] text-[#3a4a5a] leading-[1.7]">{desc}</p>
                </li>
              ))}
            </ul>
          </article>

          {/* Garanties */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Vos garanties de confiance
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Commandez vos équipements l'esprit tranquille grâce à notre système de{" "}
              <strong>paiement 100 % sécurisé</strong>. Nous vous garantissons un matériel de qualité
              professionnelle et une totale transparence sur le suivi de votre commande.
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Lock, text: "Paiement 100 % sécurisé" },
                { icon: Award, text: "Qualité professionnelle" },
                { icon: CircleCheck, text: "Transparence sur le suivi" },
              ].map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 bg-[#f7fafc] rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="text-[14.5px] text-[#0f1d2b] leading-[1.6] font-semibold mt-1">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PacksCartons;
